/**
 * Unsaved document edits kept in this browser (localStorage) so a reload does not lose them. They are
 * the person's own text on a shared device, so they are keyed by user, expire after a week, drop with the
 * versions they started from and are cleared at sign-out. Every access is guarded: storage may be blocked
 * or full, and the editor works the same without it.
 */

const PREFIX = 'wynxx.asset-draft.';
export const DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000;

interface Stored {
  text: string;
  savedAt: number;
}

export const draftKey = (userId: string, assetId: string, version: number) => `${PREFIX}${userId}.${assetId}.v${version}`;

function keysOf(userId: string): string[] {
  try {
    const own = `${PREFIX}${userId}.`;
    return Array.from({ length: localStorage.length }, (_, i) => localStorage.key(i)).filter((k): k is string => k !== null && k.startsWith(own));
  } catch {
    return [];
  }
}

export function removeDraft(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // Nothing was stored, or storage is blocked.
  }
}

/** The stored text, or null when there is none, it is unreadable or it is older than a week (then it is removed). */
export function readDraft(key: string, now = Date.now()): string | null {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return null;
    const stored = JSON.parse(raw) as Partial<Stored>;
    if (typeof stored.text === 'string' && typeof stored.savedAt === 'number' && now - stored.savedAt <= DRAFT_TTL_MS) return stored.text;
  } catch {
    // An unreadable value is as good as none.
  }
  removeDraft(key);
  return null;
}

export function writeDraft(key: string, text: string, now = Date.now()): void {
  try {
    localStorage.setItem(key, JSON.stringify({ text, savedAt: now } satisfies Stored));
  } catch {
    // Storage is a convenience; the edits are still in the page.
  }
}

/** Drops this person's expired drafts and this asset's drafts of versions older than `version`. */
export function sweepDrafts(userId: string, assetId: string, version: number, now = Date.now()): void {
  const own = `${PREFIX}${userId}.${assetId}.v`;
  for (const key of keysOf(userId)) {
    if (key.startsWith(own) && Number(key.slice(own.length)) < version) removeDraft(key);
    else readDraft(key, now);
  }
}

/** Sign-out: nothing of this person's stays on the device. */
export function clearUserDrafts(userId: string): void {
  keysOf(userId).forEach(removeDraft);
}

/** A stored value written as JSON, or null when there is none or `accept` refuses what was stored. */
export function readJsonDraft<T>(key: string, accept: (value: unknown) => value is T): T | null {
  const text = readDraft(key);
  if (text === null) return null;
  try {
    const value: unknown = JSON.parse(text);
    return accept(value) ? value : null;
  } catch {
    return null;
  }
}
